import { supabase } from '../lib/supabase';

const isSchemaError = (error) => {
  if (!error) return false;

  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') return true;
    if (errorClass === '23') return false;
  }

  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }

  return false;
};

const toCamelCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);

  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = toCamelCase(obj?.[key]);
    return acc;
  }, {});
};

export const feedbackAnalyticsService = {
  /**
   * Get average satisfaction rating from customer feedback
   * @returns {Promise<{averageRating: number, totalFeedback: number}>}
   */
  async getAverageSatisfactionRating() {
    try {
      const { data, error } = await supabase?.from('customer_feedback')?.select('satisfaction_rating');

      if (error) {
        if (isSchemaError(error)) {
          throw new Error(`Schema error: ${error?.message}. Please ensure the customer_feedback table exists.`);
        }
        throw error;
      }

      if (!data || data?.length === 0) {
        return {
          averageRating: 0,
          totalFeedback: 0
        };
      }

      const totalRating = data?.reduce((sum, item) => sum + (item?.satisfaction_rating || 0), 0);
      const averageRating = totalRating / data?.length;

      return {
        averageRating: parseFloat(averageRating?.toFixed(2)),
        totalFeedback: data?.length
      };
    } catch (error) {
      console.error('Error fetching average satisfaction rating:', error);
      throw error;
    }
  },

  /**
   * Get follow-up response rate from customer feedback
   * @returns {Promise<{responseRate: number, totalRequested: number, totalFeedback: number}>}
   */
  async getFollowUpResponseRate() {
    try {
      const { data, error } = await supabase?.from('customer_feedback')?.select('follow_up_requested');

      if (error) {
        if (isSchemaError(error)) {
          throw new Error(`Schema error: ${error?.message}. Please ensure the customer_feedback table exists.`);
        }
        throw error;
      }

      if (!data || data?.length === 0) {
        return {
          responseRate: 0,
          totalRequested: 0,
          totalFeedback: 0
        };
      }

      const totalRequested = data?.filter(item => item?.follow_up_requested === true)?.length || 0;
      const responseRate = (totalRequested / data?.length) * 100;

      return {
        responseRate: parseFloat(responseRate?.toFixed(1)),
        totalRequested,
        totalFeedback: data?.length
      };
    } catch (error) {
      console.error('Error fetching follow-up response rate:', error);
      throw error;
    }
  },

  /**
   * Get trending service issues from feedback comments
   * @param {number} limit - Maximum number of trending issues to return
   * @returns {Promise<Array<{issue: string, frequency: number}>>}
   */
  async getTrendingServiceIssues(limit = 5) {
    try {
      const { data, error } = await supabase?.from('customer_feedback')?.select('service_comments, satisfaction_rating')?.not('service_comments', 'is', null)?.lte('satisfaction_rating', 3); // Focus on lower ratings for issues

      if (error) {
        if (isSchemaError(error)) {
          throw new Error(`Schema error: ${error?.message}. Please ensure the customer_feedback table exists.`);
        }
        throw error;
      }

      if (!data || data?.length === 0) {
        return [];
      }

      // Extract keywords from comments (simple keyword extraction)
      const issueKeywords = [
        'retraso', 'demora', 'tiempo', 'espera',
        'precio', 'costo', 'caro', 'costoso',
        'calidad', 'defecto', 'problema', 'falla',
        'comunicación', 'información', 'aviso',
        'repuesto', 'pieza', 'parte',
        'limpieza', 'sucio',
        'atención', 'servicio', 'trato'
      ];

      const issueFrequency = {};

      data?.forEach(feedback => {
        const comment = feedback?.service_comments?.toLowerCase() || '';
        issueKeywords?.forEach(keyword => {
          if (comment?.includes(keyword)) {
            issueFrequency[keyword] = (issueFrequency?.[keyword] || 0) + 1;
          }
        });
      });

      // Convert to array and sort by frequency
      const trendingIssues = Object.entries(issueFrequency)
        ?.map(([issue, frequency]) => ({
          issue: issue?.charAt(0)?.toUpperCase() + issue?.slice(1),
          frequency
        }))
        ?.sort((a, b) => b?.frequency - a?.frequency)
        ?.slice(0, limit);

      return trendingIssues;
    } catch (error) {
      console.error('Error fetching trending service issues:', error);
      throw error;
    }
  },

  /**
   * Get comprehensive feedback analytics
   * @returns {Promise<{averageRating: object, responseRate: object, trendingIssues: array}>}
   */
  async getFeedbackAnalytics() {
    try {
      const [averageRating, responseRate, trendingIssues] = await Promise.all([
        this.getAverageSatisfactionRating(),
        this.getFollowUpResponseRate(),
        this.getTrendingServiceIssues(5)
      ]);

      return {
        averageRating,
        responseRate,
        trendingIssues
      };
    } catch (error) {
      console.error('Error fetching feedback analytics:', error);
      throw error;
    }
  }
};
